import { ChevronRightIcon } from "lucide-react";

import type { PolicyProcessingState } from "../../../state/policies";
import { formatCount, formatMs } from "./documentTime";

/** Label, and how to read the number: a count, or a span of time. */
const FIELDS: ReadonlyArray<{
  readonly key: keyof NonNullable<PolicyProcessingState["stats"]>;
  readonly label: string;
  readonly kind: "count" | "ms";
}> = [
  { key: "totalSections", label: "Sections", kind: "count" },
  { key: "chunksProcessed", label: "Chunks", kind: "count" },
  { key: "batchesProcessed", label: "Batches done", kind: "count" },
  { key: "batchesTotal", label: "Batches total", kind: "count" },
  { key: "imagesAnalyzed", label: "Images", kind: "count" },
  { key: "tablesAnalyzed", label: "Tables", kind: "count" },
  { key: "graphsAnalyzed", label: "Graphs", kind: "count" },
  { key: "coloredTextSegments", label: "Marked text", kind: "count" },
  { key: "conversionTimeMs", label: "Conversion", kind: "ms" },
  { key: "analysisTimeMs", label: "Analysis", kind: "ms" },
  { key: "chunkingTimeMs", label: "Chunking", kind: "ms" },
  { key: "embeddingTimeMs", label: "Embedding", kind: "ms" },
];

/**
 * What the run itself reported, as a grid rather than a JSON dump: every
 * number the worker records has a name a compliance officer can read.
 * Fields the run never reached are simply absent — not zero.
 */
export function ProcessingSummary({
  processing,
  open,
  onToggle,
}: {
  processing: PolicyProcessingState | null;
  open: boolean;
  onToggle: (open: boolean) => void;
}) {
  const stats = processing?.stats ?? null;
  if (stats === null) return null;

  const present = FIELDS.flatMap((field) => {
    const value = stats[field.key];
    if (typeof value !== "number") return [];
    return [
      { label: field.label, value: field.kind === "ms" ? formatMs(value) : formatCount(value) },
    ];
  });
  const errors = stats.validationErrors ?? [];

  return (
    <details
      className="group border-t border-border/60"
      open={open}
      onToggle={(event) => onToggle((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 hover:bg-accent/50">
        <ChevronRightIcon className="size-4 text-muted-foreground transition-transform group-open:rotate-90" />
        <span className="text-sm font-medium">Processing summary</span>
        <span className="text-xs text-muted-foreground tabular-nums">
          {present.length} measures
        </span>
      </summary>

      <div className="border-t border-border/60 px-4 py-4">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          {present.map((field) => (
            <div key={field.label}>
              <dt className="text-xs text-muted-foreground">{field.label}</dt>
              <dd className="mt-0.5 font-mono text-sm font-medium tabular-nums">{field.value}</dd>
            </div>
          ))}
        </dl>

        {errors.length > 0 ? (
          <div className="mt-4 rounded-md border border-destructive/20 bg-destructive/5 p-3">
            <p className="text-xs font-medium text-destructive">
              Validation warnings ({errors.length})
            </p>
            <ul className="mt-1.5 space-y-1 text-xs text-foreground/70">
              {errors.map((entry) => (
                <li key={entry}>• {entry}</li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </details>
  );
}
